// One color contract for the API, admin preview and storefront. No DOM dependencies.
export const APPEARANCE_FIELDS = [
  { key: 'brand', label: '品牌主色', description: '品牌標示、選取狀態與連結', default: '#74AD1D' },
  { key: 'searchBackground', label: '搜尋按鈕背景色', description: '頁首「搜尋」按鈕的背景顏色', default: '#F58B00' },
  { key: 'searchText', label: '搜尋按鈕文字色', description: '頁首「搜尋」按鈕的文字顏色，與背景分開設定', default: '#202020' },
  { key: 'secondary', label: '次要操作色', description: '加入購物車、看更多：一般為線框，滑入轉實心；回購物車平常灰色、滑入套用此色', default: '#F58B00' },
  { key: 'action', label: '主要操作色', description: '直接購買、結帳、確認結帳的背景色；不影響售價與優惠標籤', default: '#C4262D' },
  { key: 'nav', label: '主選單背景', description: '商城主選單的背景色', default: '#565039' },
  { key: 'topBackground', label: '頂部資訊列背景色', description: '回首頁、公告與會員連結所在列的背景', default: '#74AD1D' },
  { key: 'topText', label: '頂部資訊列文字色', description: '頂部資訊列的公告、文字與連結', default: '#FFFFFF' },
  { key: 'footerBackground', label: '頁尾資訊區背景色', description: '客服、政策連結與版權列的背景', default: '#74AD1D' },
  { key: 'footerText', label: '頁尾資訊區文字色', description: '頁尾標題、客服、連結與版權文字；社群圓底同步搭配', default: '#FFFFFF' },
];
export const APPEARANCE_KEYS = APPEARANCE_FIELDS.map(field => field.key);
export const APPEARANCE_DEFAULTS = Object.fromEntries(APPEARANCE_FIELDS.map(field => [field.key, field.default]));
export const APPEARANCE_PRESETS = [
  { id: 'default', label: '經典綠橘', colors: { ...APPEARANCE_DEFAULTS } },
  { id: 'minimal', label: '簡約灰藍', colors: { ...APPEARANCE_DEFAULTS, brand: '#64748B', searchBackground: '#475569', searchText:'#FFFFFF', secondary:'#475569', action: '#334155', nav: '#334155', topBackground:'#64748B', topText:'#FFFFFF', footerBackground:'#64748B', footerText:'#FFFFFF' } },
  { id: 'nature', label: '自然森綠', colors: { ...APPEARANCE_DEFAULTS, brand: '#047857', searchBackground: '#0D9488', searchText:'#FFFFFF', secondary:'#0D9488', action: '#047857', nav: '#14532D', topBackground:'#047857', topText:'#FFFFFF', footerBackground:'#047857', footerText:'#FFFFFF' } },
  { id: 'bold', label: '莓果暖紅', colors: { ...APPEARANCE_DEFAULTS, brand: '#BE123C', searchBackground: '#E11D48', searchText:'#FFFFFF', secondary:'#E11D48', action: '#BE123C', nav: '#701A35', topBackground:'#BE123C', topText:'#FFFFFF', footerBackground:'#BE123C', footerText:'#FFFFFF' } },
];
const legacyThemes = {
  default: ['#74AD1D', '#F58B00', '#565039'], minimal: ['#64748B', '#475569', '#334155'],
  nature: ['#047857', '#0D9488', '#14532D'], bold: ['#BE123C', '#E11D48', '#701A35'],
};
export const isAppearanceColor = value => typeof value === 'string' && /^#[\da-f]{6}$/i.test(value);
const object = value => value && typeof value === 'object' && !Array.isArray(value);

// Uncustomized groups deliberately leave the original CSS in charge, including
// its per-page shades. This preserves every old theme without exposing extra fields.
export function readAppearance(raw, themeId = 'default') {
  const source = object(raw) ? raw : {};
  const legacyThemeId = Object.hasOwn(legacyThemes, source.legacyThemeId) ? source.legacyThemeId
    : Object.hasOwn(legacyThemes, themeId) ? themeId : 'default';
  const [brand, accent, nav] = legacyThemes[legacyThemeId];
  const defaults = { ...APPEARANCE_DEFAULTS, brand, searchBackground:accent, secondary:accent, nav };
  const colors = Object.fromEntries(APPEARANCE_KEYS.map(key => [key,
    isAppearanceColor(source.colors && source.colors[key]) ? source.colors[key].toUpperCase() : defaults[key],
  ]));
  const groups = Array.isArray(source.customizedGroups) ? source.customizedGroups : [];
  const oldAccent = isAppearanceColor(source.colors?.accent) ? source.colors.accent.toUpperCase() : accent;
  if (!isAppearanceColor(source.colors?.searchBackground)) colors.searchBackground = oldAccent;
  if (!isAppearanceColor(source.colors?.secondary)) colors.secondary = oldAccent;
  if (!isAppearanceColor(source.colors?.searchText)) colors.searchText = groups.includes('accent') ? readable(oldAccent) : '#FFFFFF';
  const legacyBrand = (!source.schemaVersion || source.schemaVersion < 3) && groups.includes('brand');
  for (const key of ['topBackground', 'topText', 'footerBackground', 'footerText']) {
    if (!isAppearanceColor(source.colors?.[key])) colors[key] = key.endsWith('Background')
      ? (legacyBrand ? colors.brand : brand) : (legacyBrand ? readable(colors.brand) : '#FFFFFF');
  }
  return {
    schemaVersion: 5, revision: Number.isSafeInteger(source.revision) && source.revision >= 0 ? source.revision : 0,
    legacyThemeId, colors,
    customizedGroups: APPEARANCE_KEYS.filter(key => groups.includes(key) || (groups.includes('accent') && ['searchBackground','searchText','secondary'].includes(key)) || (legacyBrand && ['topBackground', 'topText', 'footerBackground', 'footerText'].includes(key))),
  };
}

export function updateAppearance(current, patch) {
  if (!object(patch) || Object.keys(patch).some(key => !['colors', 'revision', 'applyAll'].includes(key))) {
    throw new TypeError('外觀設定格式不正確');
  }
  if (!object(patch.colors) || Object.keys(patch.colors).length !== APPEARANCE_KEYS.length
    || Object.keys(patch.colors).some(key => !APPEARANCE_KEYS.includes(key))
    || APPEARANCE_KEYS.some(key => !isAppearanceColor(patch.colors[key]))) {
    throw new TypeError(`外觀設定須包含 ${APPEARANCE_KEYS.length} 個有效的 #RRGGBB 色碼`);
  }
  if (!Number.isSafeInteger(patch.revision) || patch.revision < 0
    || (patch.applyAll !== undefined && typeof patch.applyAll !== 'boolean')) throw new TypeError('外觀設定版本格式不正確');
  const previous = readAppearance(current);
  const colors = Object.fromEntries(APPEARANCE_KEYS.map(key => [key, patch.colors[key].toUpperCase()]));
  return {
    ...previous, colors, revision: previous.revision + 1,
    customizedGroups: APPEARANCE_KEYS.filter(key => patch.applyAll || previous.customizedGroups.includes(key) || colors[key] !== previous.colors[key]),
  };
}

function mix(a, b, amount) {
  return '#' + [1, 3, 5].map(i => Math.round(parseInt(a.slice(i, i + 2), 16) * (1 - amount)
    + parseInt(b.slice(i, i + 2), 16) * amount).toString(16).padStart(2, '0')).join('').toUpperCase();
}
function luminance(hex) {
  const v = hex.slice(1).match(/../g).map(x => parseInt(x, 16) / 255)
    .map(x => x <= .04045 ? x / 12.92 : ((x + .055) / 1.055) ** 2.4);
  return .2126 * v[0] + .7152 * v[1] + .0722 * v[2];
}
export function colorContrast(a, b) {
  const x = luminance(a), y = luminance(b);
  return (Math.max(x, y) + .05) / (Math.min(x, y) + .05);
}
function readable(background) { return colorContrast('#FFFFFF', background) >= colorContrast('#202020', background) ? '#FFFFFF' : '#202020'; }
function shade(color, background) {
  let result = color;
  const target = readable(background);
  for (let i = 0; i < 16; i++) {
    if (colorContrast(result, background) >= 4.5) return result;
    result = mix(result, target, .15);
  }
  return target;
}
export function resolveAppearanceColors(appearance) {
  const c = readAppearance(appearance).colors;
  return { ...c, background:'#FFFFFF', text:'#000000', muted:'#888888', border:'#DDDDDD', surface:'#F5F5F5', onBrand: readable(c.brand), onSecondary: readable(shade(c.secondary, '#FFFFFF')), secondaryInk:shade(c.secondary, '#FFFFFF'), secondaryHover:shade(c.secondary, '#FFFFFF'), searchHover:mix(c.searchBackground, '#000000', .12), onAction: readable(c.action),
    onNav: readable(c.nav), navActive: shade(mix(c.secondary, '#FFFFFF', .55), c.nav),
    actionHover: mix(c.action, '#000000', .16),
    link: shade(c.brand, '#FFFFFF'), focus: shade(c.brand, '#FFFFFF') };
}
const variableGroups = {
  brand: ['brand', 'onBrand', 'link', 'focus'], searchBackground: ['searchBackground','searchHover'], searchText:['searchText'], secondary:['secondary','secondaryInk','secondaryHover','onSecondary'],
  action: ['action', 'onAction', 'actionHover'], nav: ['nav', 'onNav', 'navActive'],
  topBackground: ['topBackground'], topText: ['topText'],
  footerBackground: ['footerBackground'], footerText: ['footerText'],
};
export function appearanceCssVariables(appearance) {
  const a = readAppearance(appearance), resolved = resolveAppearanceColors(a);
  return Object.fromEntries(a.customizedGroups.flatMap(group => variableGroups[group]
    .map(key => ['--mall-' + key.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase()), resolved[key]])));
}
